import { useEffect } from 'react';

interface FAQItem {
  question: string;
  answer: string;
}

interface HowToStep {
  name: string;
  text: string;
  image?: string;
}

// FAQPage Schema pro Google rich snippets
export const useFAQSchema = (faqs: FAQItem[]) => {
  useEffect(() => {
    const existingScript = document.querySelector('#faq-schema');
    if (existingScript) {
      existingScript.remove();
    }

    const schema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faqs.map(faq => ({
        "@type": "Question",
        "name": faq.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": faq.answer
        }
      }))
    };

    const script = document.createElement('script');
    script.id = 'faq-schema';
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      const scriptToRemove = document.querySelector('#faq-schema');
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
  }, [faqs]);
};

// HowTo Schema pro Google rich snippets
export const useHowToSchema = (
  name: string,
  description: string,
  steps: HowToStep[],
  totalTime?: string
) => {
  useEffect(() => {
    const existingScript = document.querySelector('#howto-schema');
    if (existingScript) {
      existingScript.remove();
    }

    const schema = {
      "@context": "https://schema.org",
      "@type": "HowTo",
      "name": name,
      "description": description,
      ...(totalTime && { "totalTime": totalTime }),
      "step": steps.map((step, index) => ({
        "@type": "HowToStep",
        "position": index + 1,
        "name": step.name,
        "text": step.text,
        ...(step.image && {
          "image": {
            "@type": "ImageObject",
            "url": step.image
          }
        })
      }))
    };

    const script = document.createElement('script');
    script.id = 'howto-schema';
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      const scriptToRemove = document.querySelector('#howto-schema');
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
  }, [name, description, steps, totalTime]);
};

// WebSite Schema is emitted statically in index.html as part of the sitewide
// @graph (with a stable @id), so this hook only cleans up legacy duplicates.
export const useWebSiteSchema = () => {
  useEffect(() => {
    document.querySelector('#website-schema')?.remove();
  }, []);
};


// ItemList Schema pro kolekci článků
export const useBlogListSchema = (posts: Array<{
  title: string;
  slug: string;
  image: string;
  date: string;
}>) => {
  useEffect(() => {
    const existingScript = document.querySelector('#bloglist-schema');
    if (existingScript) {
      existingScript.remove();
    }

    const schema = {
      "@context": "https://schema.org",
      "@type": "ItemList",
      "name": "Crystal Hydration Blog Articles",
      "description": "Collection of articles about crystal healing, hydration, and wellness",
      "numberOfItems": posts.length,
      "itemListElement": posts.map((post, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "item": {
          "@type": "BlogPosting",
          "headline": post.title,
          "url": `https://crystalhydrationguide.com/blog/${post.slug}`,
          "image": post.image.startsWith('http') 
            ? post.image 
            : `https://crystalhydrationguide.com${post.image}`,
          "datePublished": post.date
        }
      }))
    };

    const script = document.createElement('script');
    script.id = 'bloglist-schema';
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      const scriptToRemove = document.querySelector('#bloglist-schema');
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
  }, [posts]);
};

// Organization Schema is emitted statically in index.html as part of the
// sitewide @graph (with a stable @id); this hook only clears legacy duplicates.
export const useOrganizationSchema = () => {
  useEffect(() => {
    document.querySelector('#organization-schema')?.remove();
  }, []);
};


// Review/AggregateRating Schema for Google rich snippets (stars in search)
interface Review {
  author: string;
  reviewBody: string;
  ratingValue: number;
  datePublished?: string;
}

export const useReviewSchema = (
  reviews: Review[],
  productName: string,
  productImage?: string
) => {
  useEffect(() => {
    const existingScript = document.querySelector('#review-schema');
    if (existingScript) {
      existingScript.remove();
    }

    // Calculate average rating
    const avgRating = reviews.reduce((sum, r) => sum + r.ratingValue, 0) / reviews.length;

    const schema = {
      "@context": "https://schema.org",
      "@type": "Product",
      "name": productName,
      "image": productImage || "https://crystalhydrationguide.com/lovable-uploads/d5cbf445-bc6d-47dd-830c-39fd29f958d5.png",
      "brand": {
        "@type": "Brand",
        "name": "SlimCrystal"
      },
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": avgRating.toFixed(1),
        "bestRating": "5",
        "worstRating": "1",
        "reviewCount": reviews.length
      },
      "review": reviews.map(review => ({
        "@type": "Review",
        "author": {
          "@type": "Person",
          "name": review.author
        },
        "reviewBody": review.reviewBody,
        "reviewRating": {
          "@type": "Rating",
          "ratingValue": review.ratingValue,
          "bestRating": "5",
          "worstRating": "1"
        },
        ...(review.datePublished && { "datePublished": review.datePublished })
      }))
    };

    const script = document.createElement('script');
    script.id = 'review-schema';
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      const scriptToRemove = document.querySelector('#review-schema');
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
  }, [reviews, productName, productImage]);
};

// Speakable Schema — for voice assistants (Google Assistant, Alexa, voice search)
// Marks which CSS selectors / xpath contain content suitable for TTS read-aloud.
export const useSpeakableSchema = (cssSelectors: string[] = ['h1', 'h2', '.tl-dr', '[data-speakable]']) => {
  useEffect(() => {
    const existing = document.querySelector('#speakable-schema');
    if (existing) existing.remove();

    const schema = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "speakable": {
        "@type": "SpeakableSpecification",
        "cssSelector": cssSelectors
      },
      "url": typeof window !== 'undefined' ? window.location.href : ''
    };

    const script = document.createElement('script');
    script.id = 'speakable-schema';
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      const s = document.querySelector('#speakable-schema');
      if (s) s.remove();
    };
  }, [cssSelectors]);
};

// DefinedTerm Schema — for entity / glossary signals (GEO: helps LLMs cite definitions)
interface DefinedTerm {
  name: string;
  description: string;
  url?: string;
}

export const useDefinedTermSchema = (terms: DefinedTerm[], glossaryName = 'Crystal Hydration Glossary') => {
  useEffect(() => {
    const existing = document.querySelector('#definedterm-schema');
    if (existing) existing.remove();

    const schema = {
      "@context": "https://schema.org",
      "@type": "DefinedTermSet",
      "name": glossaryName,
      "hasDefinedTerm": terms.map(t => ({
        "@type": "DefinedTerm",
        "name": t.name,
        "description": t.description,
        ...(t.url && { "url": t.url }),
        "inDefinedTermSet": "https://crystalhydrationguide.com/faq"
      }))
    };

    const script = document.createElement('script');
    script.id = 'definedterm-schema';
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      const s = document.querySelector('#definedterm-schema');
      if (s) s.remove();
    };
  }, [terms, glossaryName]);
};
